import { Injectable, inject } from '@angular/core';
import { AbilityService } from 'ability';
import { isEqual } from 'lodash';
import { Company } from 'mbs-ui-shared';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import Computer, { AgentType, ComputerWithStatus, OsType } from 'mbs-ui-shared@models/Computer';
import { StorageAccountLegacy } from 'mbs-ui-shared@models/StorageAccount';
import { SAWithDestinations } from 'mbs-ui-shared@models/StorageAccountsWithDestinations';
import { ApplyToEnum } from 'mbs-ui-shared@models/remote-deploy/apply-to-enum';
import { ApplyToModel } from 'mbs-ui-shared@models/remote-deploy/apply-to-model';
import { ConfigParamsRelatedToChangingComputersList } from 'mbs-ui-shared@models/remote-deploy/config-params';
import { RdConfigOsTypeEnum } from 'mbs-ui-shared@models/remote-deploy/rd-config-os-type.enum';
import { RemoteDeployConfiguration } from 'mbs-ui-shared@models/remote-deploy/remote-deploy-configuration';
import { RDComputersWithStatus } from 'mbs-ui-shared@models/remote-deploy/remote-deploy-configurations-history-computer';
import { RemoteDeployDataService } from 'mbs-ui-shared@services/data-services/remote-deploy-data.service';
import { BehaviorSubject, Observable, Subscription, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, finalize, first, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class RemoteDeployDataFacade {
  private service = inject(RemoteDeployDataService);
  private ability = inject(AbilityService);
  private computersFacade = inject(ComputersFacade);

  public allStorageAccounts: StorageAccountLegacy[] = [];
  public SAWithDestinations$ = new BehaviorSubject<SAWithDestinations[]>([]);
  public rdRelatedRequestPending$ = new BehaviorSubject<boolean>(false);
  public computersLoading$ = new BehaviorSubject<boolean>(false);

  private configMap: {
    [key: string]: {
      params$: BehaviorSubject<ConfigParamsRelatedToChangingComputersList>;
      computers$: BehaviorSubject<ComputerWithStatus[]>;
      subs: Subscription[];
    };
  } = {};

  getAll(): Observable<RemoteDeployConfiguration[]> {
    return this.service.getAll().pipe(map((res) => res?.data || []));
  }

  getWithQuery(params: Record<string, any>): Observable<RemoteDeployConfiguration[]> {
    return this.service.getWithQuery(params).pipe(map((res) => res?.data || []));
  }

  getByKey(id: string): Observable<RemoteDeployConfiguration> {
    return this.service.getByKey(id);
  }

  add(config: RemoteDeployConfiguration): Observable<RemoteDeployConfiguration> {
    return this.service.add(config);
  }

  update(config: RemoteDeployConfiguration): Observable<RemoteDeployConfiguration> {
    return this.service.update(config);
  }

  delete(id: string): Observable<any> {
    return this.service.delete(id);
  }

  public getConfigSelectedParams$(config: RemoteDeployConfiguration): BehaviorSubject<ConfigParamsRelatedToChangingComputersList> {
    const confMap = this.configMap[config?.id];

    if (confMap) return confMap.params$;

    const newParamsSubj$ = new BehaviorSubject<ConfigParamsRelatedToChangingComputersList>({ os: null });
    const paramsSubscription = newParamsSubj$
      .pipe(
        filter((x) => !((x && x.os === null) || (x && x.os === undefined)) && !!this.configMap[config?.id]),
        distinctUntilChanged(
          (x, y) =>
            x.os === y.os &&
            isEqual(x.selectedCompanies, y.selectedCompanies) &&
            isEqual(x.selectedComputers, y.selectedComputers) &&
            isEqual(x.selectedTags, y.selectedTags) &&
            x.applyTo === y.applyTo
        ),
        switchMap((newParams) => {
          if (newParams.os !== RdConfigOsTypeEnum['All types']) {
            if (
              !(
                this.ability.can('read', 'SubAdmin') &&
                !this.ability.can('read', 'AccessToAllCompanies') &&
                newParams.applyTo === ApplyToEnum.ApplyToAllCompanies
              )
            ) {
              this.getDestinations(newParams as ApplyToModel, config?.id);
            } else {
              this.SAWithDestinations$.next(this.SAWithDestinations$.value);
            }
          }
          return this.getConfigComputersAggregatedRequest$(config?.id);
        })
      )
      .subscribe();

    this.configMap[config?.id] = {
      params$: newParamsSubj$,
      computers$: new BehaviorSubject<Computer[]>([]),
      subs: [paramsSubscription]
    };
    return newParamsSubj$;
  }

  patchConfigSelectedParams(config: RemoteDeployConfiguration, newParams: Partial<ConfigParamsRelatedToChangingComputersList>): void {
    const confMap = this.safeGetConfigMap(config);
    confMap.params$.next({ ...confMap.params$.value, ...newParams });
  }

  public refreshConfigComputers$(configId: string, needAddApplyTo = false, hasNBF = null, hasFFI = null): Observable<ComputerWithStatus[]> {
    return this.getConfigComputersAggregatedRequest$(configId, needAddApplyTo, hasNBF, hasFFI);
  }

  public getConfigComputers$(config: RemoteDeployConfiguration): BehaviorSubject<Computer[]> {
    return this.safeGetConfigMap(config).computers$;
  }

  clearConfigMapData(configId: string): void {
    const confMap = this.configMap[configId];
    if (confMap) {
      confMap.subs.forEach((sub) => sub.unsubscribe());
      delete this.configMap[configId];
    }
  }

  public getDestinations(ApplyTo: ApplyToModel = null, configId: string): void {
    if (ApplyTo === null) {
      ApplyTo = this.configMap[configId].params$.value as ApplyToModel;
    }
    const computers =
      ApplyTo.applyTo === ApplyToEnum.ApplyToSelectedOnly && ApplyTo.selectedComputers?.length ? ApplyTo.selectedComputers : null;
    const companies =
      ApplyTo.applyTo === ApplyToEnum.ApplyToAllSelectedCompanies && ApplyTo.selectedCompanies?.length ? ApplyTo.selectedCompanies : null;
    const tags = ApplyTo.applyTo === ApplyToEnum.ApplyToTags && ApplyTo.selectedTags?.length ? ApplyTo.selectedTags : null;

    const data = { ApplyTo: ApplyTo.applyTo, SelectedComputers: computers, SelectedCompanies: companies, SelectedTags: tags };
    if (
      ApplyTo.applyTo === ApplyToEnum.ApplyToAllCompanies ||
      (ApplyTo.applyTo === ApplyToEnum.ApplyToAllSelectedCompanies && companies) ||
      (ApplyTo.applyTo === ApplyToEnum.ApplyToSelectedOnly && computers) ||
      (ApplyTo.applyTo === ApplyToEnum.ApplyToTags && tags)
    ) {
      this.rdRelatedRequestPending$.next(true);
      this.service
        .getDestinations$(data)
        .pipe(
          first(),
          finalize(() => this.rdRelatedRequestPending$.next(false))
        )
        .subscribe((data: SAWithDestinations[]) => this.SAWithDestinations$.next(data));
    } else {
      this.SAWithDestinations$.next([]);
    }
  }

  public getConfigComputersWithParams(
    configId: string,
    status: string,
    needAddApplyTo = false,
    hasNBF = null,
    hasFFI = null
  ): Observable<RDComputersWithStatus[]> {
    const configMap = this.safeGetConfigMap({ id: configId } as RemoteDeployConfiguration);
    const osTypeVal = this.getOsTypeValueForRequest(configMap.params$.value.os, true);
    const bodyParams: any = { status, osType: osTypeVal };
    if (needAddApplyTo) {
      const p = configMap.params$.value;
      bodyParams.applyTo = p.applyTo;
      bodyParams.selectedCompanies = p.selectedCompanies || [];
      bodyParams.selectedComputers = p.selectedComputers || [];
      bodyParams.selectedTags = p.selectedTags || [];
      bodyParams.excludedComputers = p.excludedComputers || [];
    }
    if (hasNBF ?? false) bodyParams.hasNbfPlans = hasNBF;
    if (hasFFI ?? false) bodyParams.hasFfiPlans = hasFFI;
    return this.service.postConfigComputers$(configId, bodyParams);
  }

  public getTemplatesByTags(tags: number[]): Observable<{ data: RemoteDeployConfiguration[] }> {
    return this.service.getTemplatesByTags(tags);
  }

  public getConfigSelectedComputers(configuration: RemoteDeployConfiguration): Observable<Computer[]> {
    return this.service.getConfigSelectedComputers(configuration);
  }

  public getConfigSelectedCompanies(configuration: RemoteDeployConfiguration): Observable<Company[]> {
    return this.service.getConfigSelectedCompanies(configuration);
  }

  public cloneConfiguration(id: string): Observable<RemoteDeployConfiguration> {
    return this.service.cloneConfiguration(id);
  }

  public importConfiguration(computerId: string): Observable<any> {
    return this.service.importConfiguration(computerId);
  }

  public importEnabledConfiguration(computerId: string): Observable<any> {
    return this.service.importEnabledConfiguration(computerId);
  }

  public applyConfigurationToComputer(configId: string, computerId: string, enable: boolean): Observable<any> {
    return this.service.applyConfigurationToComputer(configId, computerId, enable);
  }

  private mergeComputersArray(computersWithStatuses: RDComputersWithStatus[], computers: Computer[]): ComputerWithStatus[] {
    const hash: { [key: string]: RDComputersWithStatus } = {};
    computersWithStatuses.forEach((c) => (hash[c.computerId] = c));
    if (computers?.length) {
      return computers.map((computer) => {
        const c = { ...computer } as ComputerWithStatus;
        c.lastStatus = hash[computer.id]?.lastStatus ?? null;
        c.lastStatusDate = hash[computer.id]?.lastStatusDate ?? null;
        return c;
      });
    }
    return computersWithStatuses;
  }

  private getOsTypeValueForRequest(osType: RdConfigOsTypeEnum, needFullName = false): string {
    let val = RdConfigOsTypeEnum[osType];
    if (osType === RdConfigOsTypeEnum['All types']) val = needFullName ? 'AllTypes' : 'All';
    else if (osType === RdConfigOsTypeEnum['macOS']) val = needFullName ? 'MacOS' : 'Mac';
    return val;
  }

  private getOsValueForComputersRequest(osType: RdConfigOsTypeEnum): OsType | null {
    const map: { os: RdConfigOsTypeEnum; requestOS: OsType }[] = [
      { os: RdConfigOsTypeEnum.Windows, requestOS: OsType.windows },
      { os: RdConfigOsTypeEnum.Linux, requestOS: OsType.linux },
      { os: RdConfigOsTypeEnum.macOS, requestOS: OsType.apple }
    ];
    return map.find((m) => m.os === osType)?.requestOS || null;
  }

  private safeGetConfigMap(config: RemoteDeployConfiguration) {
    if (!this.configMap[config?.id]) this.getConfigSelectedParams$(config);
    return this.configMap[config?.id];
  }

  private getConfigComputersAggregatedRequest$(
    configId: string,
    needAddApplyTo = false,
    hasNBF = null,
    hasFFI = null
  ): Observable<Computer[]> {
    const configMap = this.configMap[configId];
    if (!configMap) return of();

    const currentParams = configMap.params$.value;
    const backupRequestParams = { appIds: [AgentType.Backup], includes: ['company', 'tags'], offset: 0, limit: 10000 };
    const osTypeVal = this.getOsValueForComputersRequest(currentParams.os);
    const requestParams = osTypeVal ? { ...backupRequestParams, os: [osTypeVal] } : backupRequestParams;

    this.computersLoading$.next(true);
    const computers$ = this.computersFacade.getComputers(requestParams).pipe(
      map((response) => response.data),
      finalize(() => this.computersLoading$.next(false))
    );
    const computersWithStatus$ = configId ? this.getConfigComputersWithParams(configId, '', needAddApplyTo, hasNBF, hasFFI) : of([]);

    return combineLatest([computers$, computersWithStatus$]).pipe(
      switchMap(([computers, data]) => {
        let newComps = computers;
        if (data?.length) newComps = this.mergeComputersArray(data, computers);
        if (currentParams.applyTo === ApplyToEnum.ApplyToAllSelectedCompanies && currentParams.selectedCompanies?.length) {
          newComps = newComps.filter((x) => currentParams.selectedCompanies.includes(x.company && x.company.id));
        }
        this.configMap[configId].computers$.next(newComps);
        return of(newComps);
      })
    );
  }
}
